import { Landmark, Receipt, TriangleAlert } from "lucide-react";
import { ReportDataIssueLink } from "~/components/data/report-data-issue-link";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	useCompanyProfile,
} from "~/lib/company-profile";
import { formatBankAccount, formatDate } from "~/lib/format";
import { generateMetaTags } from "~/lib/seo";
import { getVatVerificationPresentation } from "~/lib/vat-verification";
import type { Route } from "./+types/dph";

export function meta({ matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();

	const vat = company.vatRegistration;
	const title = vat
		? `DIČ ${vat.dic} – plátce DPH – ${company.name} | FirmIndex`
		: `Plátce DPH – ${company.name} | FirmIndex`;

	return generateMetaTags({
		...companySocialMeta(company),
		title,
		description: `Registrace DPH firmy ${company.name} (IČO: ${company.ico}) — DIČ, status plátce, spolehlivost plátce DPH a zveřejněné bankovní účty.`,
		canonical: companyCanonicalUrl(company, "dph"),
		noindex: true,
	});
}

export function headers() {
	return { "X-Robots-Tag": "noindex, nofollow" };
}

export default function CompanyDphRoute() {
	const company = useCompanyProfile();
	const vat = company.vatRegistration;

	if (!vat) {
		const vatWasChecked = Boolean(company.dataFreshness?.vat);

		return (
			<div className="py-8 text-center">
				<Receipt className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm font-medium text-muted-foreground">
					{vatWasChecked
						? "Ověřeno – firma není plátce DPH."
						: "Údaje o registraci DPH nejsou k dispozici."}
				</p>
				<p className="text-xs text-muted-foreground mt-1">
					{vatWasChecked
						? "Firma není v ADIS vedena jako plátce DPH ani identifikovaná osoba."
						: "Údaje zatím nemáme zpracované."}
				</p>
			</div>
		);
	}

	const accounts = vat.bankAccounts ?? [];
	const verificationCurrent = vat.verificationStatus === "VERIFIED";
	const verification = getVatVerificationPresentation(vat);

	return (
		<div className="flex flex-col gap-4 pt-4">
			{/* Unreliable payer alert */}
			{vat.isUnreliable && verificationCurrent && (
				<div className="flex items-center gap-3 rounded-xl bg-red-50 p-4 ring-1 ring-red-100">
					<TriangleAlert className="h-4 w-4 shrink-0 text-red-600" />
					<div className="flex flex-col gap-0.5">
						<p className="text-sm font-medium text-red-800">Nespolehlivý plátce DPH</p>
						<p className="text-xs text-red-700">
							Firma je vedena v registru nespolehlivých plátců DPH
							{vat.unreliableSince && ` od ${formatDate(vat.unreliableSince)}`}. Odběratelé ručí za
							DPH neodvedenou z přijatých zdanitelných plnění.
						</p>
					</div>
				</div>
			)}
			{!verificationCurrent && (
				<div className="card-tonal rounded-xl p-4">
					<div className="flex items-start gap-3">
						<TriangleAlert className="h-4 w-4 shrink-0 text-muted mt-0.5" />
						<div className="flex flex-col gap-0.5">
							<p className="text-sm font-medium">Spolehlivost plátce není aktuálně ověřena</p>
							<p className="text-xs text-muted">
								Rizikový příznak nezobrazujeme, dokud nepotvrdíme aktuální stav v ADIS.
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Registration block */}
			<div className="card-surface rounded-xl p-6">
				<div className="flex items-center gap-2 mb-4">
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
						<Receipt className="h-4 w-4 text-primary-600" />
					</div>
					<h2 className="text-lg font-semibold">Registrace DPH</h2>
					<span className={`${verification.className} ml-auto`}>{verification.label}</span>
				</div>

				<dl className="flex flex-col gap-3 text-sm">
					<DataRow label="DIČ" value={vat.dic} mono />
					<DataRow label="Typ" value={vat.isVatPayer ? "Plátce DPH" : "Není plátce DPH"} />
					{vat.registeredAt && <DataRow label="Platná od" value={formatDate(vat.registeredAt)} />}
					{vat.deregisteredAt && (
						<DataRow label="Registrace ukončena" value={formatDate(vat.deregisteredAt)} />
					)}
					{vat.isUnreliable && verificationCurrent && (
						<div className="flex items-baseline gap-2">
							<dt className="text-muted-foreground w-40 shrink-0">Nespolehlivý plátce</dt>
							<dd className="font-medium text-red-600">
								Ano
								{vat.unreliableSince && ` (od ${formatDate(vat.unreliableSince)})`}
							</dd>
						</div>
					)}
					{vat.taxOfficeCode && (
						<DataRow label="Finanční úřad" value={`FÚ kód ${vat.taxOfficeCode}`} />
					)}
				</dl>
				<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
					{vat.lastCheckedAt && (
						<span className="text-xs text-muted">
							Naposledy ověřeno {formatDate(vat.lastCheckedAt)}
						</span>
					)}
					<ReportDataIssueLink
						subject="VAT"
						companyIco={company.ico}
						companyName={company.name}
						returnTo={`/firma/${company.ico}/dph`}
						className="btn btn-ghost btn-xs ml-auto"
					/>
				</div>
			</div>

			{/* Published bank accounts */}
			{accounts.length > 0 && (
				<div className="card-surface rounded-xl p-6">
					<div className="flex items-center gap-2 mb-4">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
							<Landmark className="h-4 w-4 text-primary-600" />
						</div>
						<h2 className="text-lg font-semibold">Bankovní účty určené ke zveřejnění</h2>
					</div>

					<div className="card-surface rounded-xl overflow-x-auto">
						<table className="w-full min-w-max">
							<thead className="bg-gray-100">
								<tr className="border-b border-gray-200">
									<th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Účet</th>
									<th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">
										Datum zveřejnění
									</th>
									<th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">
										Datum ukončení
									</th>
								</tr>
							</thead>
							<tbody>
								{accounts.map((account) => (
									<tr
										key={`${account.prefix ?? ""}-${account.number}`}
										className="border-b border-gray-100"
									>
										<td className="py-3 px-4 font-mono text-sm tabular-nums">
											{formatBankAccount(account)}
										</td>
										<td className="py-3 px-4 text-sm">{formatDate(account.publishedDate)}</td>
										<td className="py-3 px-4 text-sm">
											{account.endDate ? formatDate(account.endDate) : "–"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<p className="mt-4 text-xs text-muted-foreground">
						Platba na jiný než zveřejněný účet zakládá ručení odběratele za DPH nezaplacenou
						dodavatelem (§ 109 odst. 2 písm. c) zákona o DPH). Před úhradou faktury doporučujeme
						ověřit, že bankovní účet dodavatele je uveden v tomto seznamu.
					</p>
				</div>
			)}
		</div>
	);
}

function DataRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
	return (
		<div className="flex items-baseline gap-2">
			<dt className="text-muted-foreground w-40 shrink-0">{label}</dt>
			<dd className={mono ? "font-mono" : ""}>{value}</dd>
		</div>
	);
}
